import type { FunctionComponent } from 'react';

import { Entitlements } from '@trello/entitlements';
import type { Organization } from '@trello/model-types';
import { EnterpriseIcon } from '@trello/nachos/icons/enterprise';
import { PrivateIcon } from '@trello/nachos/icons/private';
import { PublicIcon } from '@trello/nachos/icons/public';
import { token } from '@trello/theme';

import { useMemberActivityWorkspacesFragment } from './MemberActivityWorkspacesFragment.generated';

import * as styles from './MemberActivityWorkspaces.module.less';

interface MemberActivityWorkspacesProps {
  memberId: string;
  isMemberMe: boolean;
}

function getWorkspaceIcon(permissionLevel?: string) {
  if (permissionLevel === 'private') {
    return (
      <PrivateIcon
        size="small"
        color={token('color.icon.accent.red', '#C9372C')}
      />
    );
  }
  return (
    <PublicIcon
      size="small"
      color={token('color.icon.accent.green', '#22A06B')}
    />
  );
}

export const MemberActivityWorkspaces: FunctionComponent<
  MemberActivityWorkspacesProps
> = ({ memberId, isMemberMe }) => {
  const { data: member } = useMemberActivityWorkspacesFragment({
    from: {
      id: memberId,
    },
  });

  const organizations = member?.organizations;
  const alphabetizedOrganizations = organizations
    ? [...organizations].sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      )
    : undefined;

  const isPaidWorkspace = (org: Pick<Organization, 'offering'>) => {
    return org.offering && !Entitlements.isFree(org.offering);
  };

  return (
    <div className={styles.ulGutter}>
      <ul className={styles.orgList}>
        {alphabetizedOrganizations?.map((org) => {
          return (
            (org.prefs?.permissionLevel === 'public' || isMemberMe) && (
              <li key={org.id}>
                <a href={`/w/${org.name}`}>
                  <span className={styles.itemName}>{org.displayName}</span>
                  {isPaidWorkspace(org) && (
                    <span className={styles.itemIcon}>
                      <EnterpriseIcon size="small" />
                    </span>
                  )}
                  <span className={styles.itemIcon}>
                    {getWorkspaceIcon(org.prefs?.permissionLevel || undefined)}
                  </span>
                </a>
              </li>
            )
          );
        })}
      </ul>
    </div>
  );
};
